import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS service（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import Input from "../../components/form-item/Input";
import Select from "../../components/form-item/Select";
import Avatar from "../../components/avatar/Avatar";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import {
  AdminProjectSummary,
  useLazySearchAdminProjectsQuery,
} from "../../app/services/adminAcr";
import {
  DemoLandingTab,
  DemoLink,
  DemoLinkMember,
  useCreateDemoLinkMutation,
  useDeleteDemoLinkMutation,
  useGetDemoLinkMembersQuery,
  useGetDemoLinksQuery,
  useUpdateDemoLinkMutation,
} from "../../app/services/demo";
import { LANDING_TAB_LABELS, POSITION_LABELS } from "../../utils/demoMode";

/** プロジェクト検索のデバウンス間隔（ms）。 */
const SEARCH_DEBOUNCE_MS = 250;
/** プロジェクト検索の取得件数。 */
const SEARCH_LIMIT = 30;

/** 有効期限プリセット（発行フォームの Select 用） */
const EXPIRES_OPTIONS = [
  { value: "", name: "無期限" },
  { value: "7", name: "7日間" },
  { value: "30", name: "30日間" },
  { value: "90", name: "90日間" },
];

/** 初回表示画面の選択肢（BE: DemoLink.LANDING_TAB_CHOICES と対応） */
const LANDING_TAB_OPTIONS = Object.entries(LANDING_TAB_LABELS).map(
  ([value, name]) => ({ value, name }),
);

const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap24",
})`
  padding: 24px 16px 80px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.section.attrs({
  className: "u-col u-w100 u-gap12",
})``;

const ListWrapper = styled.div.attrs({
  className: "u-col u-w100",
})`
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  max-height: 240px;
  overflow-y: auto;
`;

const SelectableRow = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-bg-blue, rgba(0, 154, 206, 0.06))" : "transparent"};
  cursor: pointer;
  text-align: left;
  border-bottom: 1px solid var(--soremo-border);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--soremo-background, #fafafa);
  }
`;

const SelectedCard = styled.div.attrs({
  className: "u-row u-items-center u-w100 u-gap12",
})`
  padding: 12px;
  border: 1px solid var(--soremo-blue);
  background: var(--soremo-bg-blue, rgba(0, 154, 206, 0.06));
  border-radius: 6px;
`;

const NoticeCard = styled.div.attrs({
  className: "u-col u-w100 u-gap4",
})`
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: var(--soremo-background, #fafafa);
`;

const IssueForm = styled.div.attrs({
  className: "u-row u-items-end u-w100 u-gap12",
})`
  flex-wrap: wrap;

  > .issue-form-label {
    flex: 1;
    min-width: 240px;
  }
`;

const LinkCard = styled.div.attrs({
  className: "u-col u-w100 u-gap8",
})<{ $inactive?: boolean }>`
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  opacity: ${({ $inactive }) => ($inactive ? 0.6 : 1)};
`;

const LinkCardHeader = styled.div.attrs({
  className: "u-row-between u-items-center u-w100 u-gap8",
})``;

const LinkCardMeta = styled.div.attrs({
  className: "u-row u-items-center u-gap16",
})`
  flex-wrap: wrap;
`;

const UrlRow = styled.div.attrs({
  className: "u-row u-items-center u-w100 u-gap8",
})`
  padding: 8px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  background: var(--soremo-background, #fafafa);

  .demo-url-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StatusBadge = styled.span<{ $available: boolean }>`
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  color: #fff;
  background: ${({ $available }) =>
    $available ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
`;

/** 日時の簡易表示（YYYY/MM/DD HH:mm）。null は "-"。 */
const formatDateTime = (iso: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** デモリンクの完全 URL */
const buildDemoUrl = (token: string): string =>
  `${window.location.origin}/demo/${token}`;

/**
 * 管理者向けデモリンク管理ページ（/console/demo）。
 *
 * プロジェクトと「どのメンバーとして見せるか」を指定して、サインインなしで
 * 閲覧できる外部公開向けデモリンク（/demo/:token）を発行する。ロールは
 * 選んだメンバーのプロジェクト内ポジションから自動決定されるため、
 * ロール選択 UI は無い。
 *
 * 発行するとそのメンバーはデモ閲覧アカウント（is_demo）になり、
 * 通常サインイン不可・API への書き込み遮断となる。実在のメンバーを
 * 誤って選ばないよう、一覧に is_demo 状態と注意書きを表示する。
 * 権限は `role === "ADMIN"`（master_admin）のみ。
 */
const AdminDemoLinks: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  // ===== プロジェクト検索 / 選択 =====
  const [searchInput, setSearchInput] = useState("");
  const debouncedQuery = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();

  const [selectedProject, setSelectedProject] =
    useState<AdminProjectSummary | null>(null);
  const [searchResults, setSearchResults] = useState<AdminProjectSummary[]>([]);

  const [searchProjects, { isFetching: isSearching }] =
    useLazySearchAdminProjectsQuery();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await searchProjects({
          q: trimmedQuery,
          limit: SEARCH_LIMIT,
          offset: 0,
        }).unwrap();
        if (!cancelled) {
          setSearchResults(resp.data.projects);
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trimmedQuery, searchProjects]);

  // ===== メンバー選択（誰として見せるか） =====
  const projectId = selectedProject?.project_id ?? null;
  const [selectedMember, setSelectedMember] = useState<DemoLinkMember | null>(
    null,
  );

  const { data: membersData, isFetching: isMembersFetching } =
    useGetDemoLinkMembersQuery(projectId ?? "", { skip: !projectId });
  const members = membersData?.data.members ?? [];

  const handleSelectProject = useCallback((project: AdminProjectSummary) => {
    setSelectedProject(project);
    setSelectedMember(null);
  }, []);

  // ===== 発行フォーム =====
  const [label, setLabel] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [landingTab, setLandingTab] = useState<DemoLandingTab>("home");
  const [createDemoLink, { isLoading: isCreating }] =
    useCreateDemoLinkMutation();

  // スペシャリストは DM しか閲覧できず、ProjectWrapper が初期タブを
  // 強制的に DM へ切り替えるため、初回表示画面の指定は無効になる。
  const isSpecialistSelected = selectedMember?.position === "SPECIALIST";
  useEffect(() => {
    if (isSpecialistSelected) {
      setLandingTab("home");
    }
  }, [isSpecialistSelected]);

  const { copy } = useCopyToClipboard({
    successMessage: "デモリンクをコピーしました",
    errorMessage: "コピーに失敗しました",
  });

  const handleCreate = useCallback(async () => {
    if (!projectId || !selectedMember || isCreating) return;
    try {
      const expiresAt = expiresDays
        ? new Date(
            Date.now() + Number(expiresDays) * 24 * 60 * 60 * 1000,
          ).toISOString()
        : null;
      const resp = await createDemoLink({
        project_id: projectId,
        user_id: selectedMember.id,
        label: label.trim(),
        landing_tab: landingTab,
        expires_at: expiresAt,
      }).unwrap();
      toast.success("デモリンクを発行しました");
      setLabel("");
      setLandingTab("home");
      // 選択中カードの「デモ利用中」表示を即時反映する
      // （一覧は invalidatesTags で取り直されるが、選択中はローカルコピーのため）
      setSelectedMember((member) =>
        member ? { ...member, is_demo: true } : member,
      );
      // 発行した URL をすぐ配布できるようにクリップボードへ
      void copy(buildDemoUrl(resp.data.token));
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "デモリンクの発行に失敗しました";
      toast.error(message);
    }
  }, [projectId, selectedMember, isCreating, expiresDays, label, landingTab, createDemoLink, copy]);

  // ===== 発行済みリンク一覧 =====
  const { data: linksData, isLoading: isLinksLoading } = useGetDemoLinksQuery();
  const demoLinks = linksData?.data.demo_links ?? [];

  const [updateDemoLink] = useUpdateDemoLinkMutation();
  const [deleteDemoLink, { isLoading: isDeleting }] =
    useDeleteDemoLinkMutation();
  const [deleteTarget, setDeleteTarget] = useState<DemoLink | null>(null);

  const handleToggleActive = useCallback(
    async (link: DemoLink) => {
      try {
        await updateDemoLink({
          demo_link_id: link.demo_link_id,
          is_active: !link.is_active,
        }).unwrap();
        toast.success(
          link.is_active ? "リンクを無効化しました" : "リンクを有効化しました",
        );
      } catch {
        toast.error("リンクの更新に失敗しました");
      }
    },
    [updateDemoLink],
  );

  const handleChangeLandingTab = useCallback(
    async (link: DemoLink, nextTab: string) => {
      if (nextTab === link.landing_tab) return;
      try {
        await updateDemoLink({
          demo_link_id: link.demo_link_id,
          landing_tab: nextTab as DemoLandingTab,
        }).unwrap();
        toast.success("初回表示画面を変更しました");
      } catch {
        toast.error("初回表示画面の変更に失敗しました");
      }
    },
    [updateDemoLink],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteDemoLink(deleteTarget.demo_link_id).unwrap();
      toast.success("デモリンクを削除しました");
    } catch {
      toast.error("デモリンクの削除に失敗しました");
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteDemoLink]);

  // ===== 権限ガード =====
  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }
  const role: string | undefined = currentUser?.data?.role;
  if (role !== "ADMIN") {
    return <Navigate to="/projects" replace />;
  }

  return (
    <PageWrapper>
      <TitleBlock
        title="デモリンク管理"
        description="サインインなしで閲覧できる外部公開向けデモリンクを発行します。プロジェクトと「誰として見せるか」を指定すると、そのメンバーのロールで閲覧専用のデモサイトとして機能します。デモ中の書き込み（コメント送信・アップロード・編集）はすべて遮断されます。デモはブラウザのサインイン状態を専用の閲覧アカウントに切り替えるため、発行したリンクを自分で確認するときはシークレットウィンドウで開いてください（同じウィンドウで開くとサインアウトを求められます）。"
      />

      {/* ===== 1. プロジェクト選択 ===== */}
      <Section>
        <h2 className="heading-13">1. プロジェクトを選択</h2>
        <p className="bodytext-11 u-text-light-gray">
          外部公開しても問題のない、デモ専用のサンプルプロジェクトを指定してください。
        </p>
        <Input
          type="search"
          placeholder="プロジェクト名・コード名で絞り込み"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {selectedProject && (
          <SelectedCard>
            <div className="u-col u-fill u-gap2">
              <span className="heading-13">{selectedProject.name}</span>
              <span className="bodytext-11 u-text-light-gray">
                ID: {selectedProject.project_id}
                {selectedProject.code_name
                  ? ` / ${selectedProject.code_name}`
                  : ""}
              </span>
            </div>
            <Button
              size="small"
              theme="tertiary"
              variant="text"
              onClick={() => {
                setSelectedProject(null);
                setSelectedMember(null);
              }}
            >
              選択を解除
            </Button>
          </SelectedCard>
        )}
        {!selectedProject && (
          <ListWrapper>
            {isSearching && searchResults.length === 0 ? (
              <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                読み込み中...
              </p>
            ) : searchResults.length === 0 ? (
              <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                該当するプロジェクトがありません。
              </p>
            ) : (
              searchResults.map((project) => (
                <SelectableRow
                  key={project.project_id}
                  type="button"
                  onClick={() => handleSelectProject(project)}
                >
                  <span className="bodytext-13">{project.name}</span>
                  <span className="bodytext-11 u-text-light-gray">
                    {project.code_name}
                  </span>
                </SelectableRow>
              ))
            )}
          </ListWrapper>
        )}
      </Section>

      {/* ===== 2. メンバー選択（ロールは自動決定） ===== */}
      {selectedProject && (
        <Section>
          <h2 className="heading-13">2. 誰として見せるかを選択</h2>
          <NoticeCard>
            <span className="bodytext-11">
              閲覧者は選んだメンバー本人としてプロジェクトを見ます（ロールはそのメンバーのポジションで自動決定）。
            </span>
            <span className="bodytext-11 u-text-green">
              発行するとそのアカウントはデモ専用になり、通常サインインができなくなります。必ず架空のダミーアカウントを選んでください（実在メンバーは選ばない）。
            </span>
          </NoticeCard>
          {selectedMember ? (
            <SelectedCard>
              <Avatar size={32} src={selectedMember.avatar_url ?? undefined} />
              <div className="u-col u-fill u-gap2">
                <span className="heading-13">{selectedMember.display_name}</span>
                <span className="bodytext-11 u-text-light-gray">
                  ロール:{" "}
                  {selectedMember.position
                    ? (POSITION_LABELS[selectedMember.position] ?? selectedMember.position)
                    : "-"}
                  {selectedMember.is_demo && " / デモ利用中"}
                </span>
              </div>
              <Button
                size="small"
                theme="tertiary"
                variant="text"
                onClick={() => setSelectedMember(null)}
              >
                選択を解除
              </Button>
            </SelectedCard>
          ) : (
            <ListWrapper>
              {isMembersFetching ? (
                <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                  読み込み中...
                </p>
              ) : members.length === 0 ? (
                <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                  選択できるメンバーがいません（管理者・マネージャーは指定できません）。
                </p>
              ) : (
                members.map((member) => (
                  <SelectableRow
                    key={member.id}
                    type="button"
                    onClick={() => setSelectedMember(member)}
                  >
                    <span className="u-row u-items-center u-gap8">
                      <Avatar size={32} src={member.avatar_url ?? undefined} />
                      <span className="bodytext-13">{member.display_name}</span>
                    </span>
                    <span className="bodytext-11 u-text-light-gray">
                      {member.position
                        ? (POSITION_LABELS[member.position] ?? member.position)
                        : "-"}
                      {member.is_demo && " / デモ利用中"}
                    </span>
                  </SelectableRow>
                ))
              )}
            </ListWrapper>
          )}
        </Section>
      )}

      {/* ===== 3. 発行 ===== */}
      {selectedProject && selectedMember && (
        <Section>
          <h2 className="heading-13">3. リンクを発行</h2>
          <IssueForm>
            <div className="issue-form-label">
              <Input
                label="管理用ラベル"
                option="optional"
                placeholder="例: ◯◯社向けデモ"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <Select
              label="初回表示画面"
              width="160px"
              value={landingTab}
              options={LANDING_TAB_OPTIONS}
              onChange={(e) => setLandingTab(e.target.value as DemoLandingTab)}
              disabled={isSpecialistSelected}
            />
            <Select
              label="有効期限"
              width="160px"
              value={expiresDays}
              options={EXPIRES_OPTIONS}
              onChange={(e) => setExpiresDays(e.target.value)}
            />
            <Button
              size="small"
              theme="primary"
              variant="filled"
              leftIcon="add_link"
              onClick={() => void handleCreate()}
              disabled={isCreating}
            >
              {isCreating ? "発行中..." : "デモリンクを発行"}
            </Button>
          </IssueForm>
          {isSpecialistSelected && (
            <p className="bodytext-11 u-text-light-gray">
              スペシャリストとして見せる場合は DM のみ閲覧できるため、初回表示画面は指定できません（常に DM が表示されます）。
            </p>
          )}
        </Section>
      )}

      {/* ===== 4. 発行済みリンク一覧 ===== */}
      <Section>
        <h2 className="heading-13">発行済みのデモリンク</h2>
        {isLinksLoading ? (
          <SvgLoadingAnimation />
        ) : demoLinks.length === 0 ? (
          <p className="bodytext-13 u-text-light-gray">
            発行済みのデモリンクはありません。
          </p>
        ) : (
          demoLinks.map((link) => (
            <LinkCard key={link.demo_link_id} $inactive={!link.is_available}>
              <LinkCardHeader>
                <span className="u-row u-items-center u-gap8">
                  <StatusBadge className="bodytext-11" $available={link.is_available}>
                    {link.is_available
                      ? "有効"
                      : link.is_active
                        ? "期限切れ"
                        : "無効"}
                  </StatusBadge>
                  <span className="heading-13">
                    {link.label || "（ラベルなし）"}
                  </span>
                </span>
                <span className="u-row u-items-center u-gap8">
                  <Button
                    size="small"
                    theme="secondary"
                    variant="outlined"
                    width="fit"
                    onClick={() => void handleToggleActive(link)}
                  >
                    {link.is_active ? "無効化" : "再有効化"}
                  </Button>
                  <IconButton
                    icon="delete"
                    size={20}
                    title="削除"
                    ariaLabel="デモリンクを削除"
                    onClick={() => setDeleteTarget(link)}
                  />
                </span>
              </LinkCardHeader>

              <LinkCardMeta>
                <span className="bodytext-11 u-text-light-gray">
                  プロジェクト: {link.project.name}
                </span>
                <span className="bodytext-11 u-text-light-gray">
                  閲覧メンバー: {link.demo_user.display_name}
                  {link.demo_user.position
                    ? `（${POSITION_LABELS[link.demo_user.position] ?? link.demo_user.position}）`
                    : ""}
                </span>
                <span className="u-row u-items-center u-gap4 bodytext-11 u-text-light-gray">
                  初回表示:
                  {link.demo_user.position === "SPECIALIST" ? (
                    // スペシャリストは常に DM が初期表示のため変更させない
                    <span>DM（固定）</span>
                  ) : (
                    <span style={{ width: 120 }}>
                      <Select
                        size="small"
                        name={`landing-tab-${link.demo_link_id}`}
                        value={link.landing_tab}
                        options={LANDING_TAB_OPTIONS}
                        onChange={(e) =>
                          void handleChangeLandingTab(link, e.target.value)
                        }
                      />
                    </span>
                  )}
                </span>
                <span className="bodytext-11 u-text-light-gray">
                  有効期限: {link.expires_at ? formatDateTime(link.expires_at) : "無期限"}
                </span>
                <span className="bodytext-11 u-text-light-gray">
                  アクセス: {link.access_count}回
                  {link.last_accessed_at
                    ? `（最終 ${formatDateTime(link.last_accessed_at)}）`
                    : ""}
                </span>
              </LinkCardMeta>

              <UrlRow>
                <span className="demo-url-text bodytext-13">
                  {buildDemoUrl(link.token)}
                </span>
                <IconButton
                  icon="content_copy"
                  size={18}
                  title="URL をコピー"
                  ariaLabel="デモリンクの URL をコピー"
                  onClick={() => void copy(buildDemoUrl(link.token))}
                />
              </UrlRow>
            </LinkCard>
          ))
        )}
      </Section>

      {deleteTarget && (
        <ConfirmModal
          message={`「${deleteTarget.label || buildDemoUrl(deleteTarget.token)}」を削除しますか？\nこのリンクからのアクセスは即時できなくなります。`}
          confirmLabel="削除"
          onConfirm={() => void handleDelete()}
          onCancel={() => setDeleteTarget(null)}
          loading={isDeleting}
        />
      )}
    </PageWrapper>
  );
};

export default AdminDemoLinks;
